import { ColorValue } from 'react-native'

// eslint-disable-next-line @typescript-eslint/naming-convention
export enum THEME_TYPES {
  LIGHT = 'light',
  DARK = 'dark',
  SYSTEM = 'system'
}

export type ThemeType = THEME_TYPES.LIGHT | THEME_TYPES.DARK | THEME_TYPES.SYSTEM

export const DEFAULT_THEME = THEME_TYPES.DARK

export type ThemeProps = {
  [key in keyof typeof ThemeColors]: ColorValue
}

const ThemeColors = {
  primary: {
    [THEME_TYPES.DARK]: '#027BFF',
    [THEME_TYPES.LIGHT]: '#027BFF'
  },
  primary20: {
    [THEME_TYPES.DARK]: '#027BFF33',
    [THEME_TYPES.LIGHT]: '#027BFF14'
  },
  primaryLight: {
    [THEME_TYPES.DARK]: '#0B3230',
    [THEME_TYPES.LIGHT]: '#B8DCFF'
  },
  primaryLight80: {
    [THEME_TYPES.DARK]: '#66B1FF80',
    [THEME_TYPES.LIGHT]: '#027BFF80'
  },
  primaryText: {
    [THEME_TYPES.DARK]: '#FFFFFF',
    [THEME_TYPES.LIGHT]: '#141833'
  },
  secondaryText: {
    [THEME_TYPES.DARK]: '#818997',
    [THEME_TYPES.LIGHT]: '#54597A'
  },
  tertiaryText: {
    [THEME_TYPES.DARK]: '#818181',
    [THEME_TYPES.LIGHT]: '#767DAD'
  },
  linkText: {
    [THEME_TYPES.DARK]: '#70B4FF',
    [THEME_TYPES.LIGHT]: '#027BFF'
  },
  primaryBorder: {
    [THEME_TYPES.DARK]: '#FFFFFF1F',
    [THEME_TYPES.LIGHT]: '#767DAD'
  },
  secondaryBorder: {
    [THEME_TYPES.DARK]: '#FFFFFF52',
    [THEME_TYPES.LIGHT]: '#CACDE6'
  },
  primaryBackground: {
    [THEME_TYPES.DARK]: '#0D0D0F',
    [THEME_TYPES.LIGHT]: '#FFFFFF'
  },
  primaryBackgroundInverted: {
    [THEME_TYPES.DARK]: '#FFFFFF',
    [THEME_TYPES.LIGHT]: '#141833'
  },
  secondaryBackground: {
    [THEME_TYPES.DARK]: '#111318',
    [THEME_TYPES.LIGHT]: '#F2F3FA'
  },
  secondaryBackgroundInverted: {
    [THEME_TYPES.DARK]: '#F2F3FA',
    [THEME_TYPES.LIGHT]: '#111318'
  },
  tertiaryBackground: {
    [THEME_TYPES.DARK]: '#0F1F2E',
    [THEME_TYPES.LIGHT]: '#E8F0FF'
  },
  quaternaryBackground: {
    [THEME_TYPES.DARK]: '#FFFFFF20',
    [THEME_TYPES.LIGHT]: '#767DAD16'
  },
  quaternaryBackgroundSolid: {
    [THEME_TYPES.DARK]: '#111318',
    [THEME_TYPES.LIGHT]: '#F4F5F8'
  },
  quinaryBackground: {
    [THEME_TYPES.DARK]: '#0D0D0F',
    [THEME_TYPES.LIGHT]: '#F7F8FC'
  },
  senaryBackground: {
    [THEME_TYPES.DARK]: '#242424',
    [THEME_TYPES.LIGHT]: '#242424'
  },
  septenaryBackground: {
    [THEME_TYPES.DARK]: '#1F1F1F',
    [THEME_TYPES.LIGHT]: '#F7F8FC'
  },
  octonaryBackground: {
    [THEME_TYPES.DARK]: '#0D0D0E',
    [THEME_TYPES.LIGHT]: '#FFFFFF'
  },
  backdrop: {
    [THEME_TYPES.DARK]: '#0D0D0F95',
    [THEME_TYPES.LIGHT]: '#54597ACC'
  },
  // Success
  successText: {
    [THEME_TYPES.DARK]: '#31D158',
    [THEME_TYPES.LIGHT]: '#006D3F'
  },
  successDecorative: {
    [THEME_TYPES.DARK]: '#31D158',
    [THEME_TYPES.LIGHT]: '#018649'
  },
  successBackground: {
    [THEME_TYPES.DARK]: '#1d2a1f',
    [THEME_TYPES.LIGHT]: '#EBF5F0'
  },
  // Info
  infoText: {
    [THEME_TYPES.DARK]: '#70B4FF',
    [THEME_TYPES.LIGHT]: '#0750A1'
  },
  infoDecorative: {
    [THEME_TYPES.DARK]: '#70B4FF',
    [THEME_TYPES.LIGHT]: '#027BFF'
  },
  infoBackground: {
    [THEME_TYPES.DARK]: '#1b212b',
    [THEME_TYPES.LIGHT]: '#EAF3FF'
  },
  // Accent chip / batch-action surface (mobile-web send design). Dark = saturated
  // navy fill + bright blue accent (border for the batch button, text for the
  // Max / "Add to address book" pills). Light keeps the subtle primary tint so
  // other builds stay visually unchanged.
  accentChipBackground: {
    [THEME_TYPES.DARK]: '#001D4D',
    [THEME_TYPES.LIGHT]: '#027BFF14'
  },
  accentChipDecorative: {
    [THEME_TYPES.DARK]: '#0291FF',
    [THEME_TYPES.LIGHT]: '#027BFF'
  },
  // Elevated input/control surface (the "Pay fee with" design: fee-speed pill and
  // fee-token select row). Dark = a panel one step above the secondaryBackground
  // card; light = the neutral grey input fill. Generic, reuse for other carded
  // controls that sit on top of a secondaryBackground surface.
  controlSurfaceBackground: {
    [THEME_TYPES.DARK]: '#191D24',
    [THEME_TYPES.LIGHT]: '#F2F3FA'
  },
  controlSurfaceBorder: {
    [THEME_TYPES.DARK]: '#22272F',
    [THEME_TYPES.LIGHT]: '#CACDE6'
  },
  // Active/selected border for a Select trigger while its dropdown is open
  // (token picker in the Send/Shield/Unshield cards). Brighter than the resting
  // controlSurfaceBorder so the open control reads as focused.
  selectActiveBorder: {
    [THEME_TYPES.DARK]: '#3A6CFF',
    [THEME_TYPES.LIGHT]: '#027BFF'
  },
  // Filled destructive button (Reject). Dark = saturated maroon fill (Figma
  // Negative-Tertiary) behind white text + red border; light = soft red fill
  // behind dark-red text. Border stays errorDecorative in both modes.
  dangerBackground: {
    [THEME_TYPES.DARK]: '#47000D',
    [THEME_TYPES.LIGHT]: '#FEEBEE'
  },
  // Info 2
  info2Text: {
    [THEME_TYPES.DARK]: '#70B4FF',
    [THEME_TYPES.LIGHT]: '#0750A1'
  },
  info2Decorative: {
    [THEME_TYPES.DARK]: '#70B4FF',
    [THEME_TYPES.LIGHT]: '#0079FF'
  },
  info2Background: {
    [THEME_TYPES.DARK]: '#70B4FF1F',
    [THEME_TYPES.LIGHT]: '#0079FF14'
  },
  // Info 3
  info3Text: {
    [THEME_TYPES.DARK]: '#ffffff',
    [THEME_TYPES.LIGHT]: '#54597A'
  },
  info3Decorative: {
    [THEME_TYPES.DARK]: '#70B4FF',
    [THEME_TYPES.LIGHT]: '#0079ff'
  },
  info3Background: {
    [THEME_TYPES.DARK]: '#2A323D',
    [THEME_TYPES.LIGHT]: '#EBF4FF'
  },
  info3Button: {
    [THEME_TYPES.DARK]: '#70B4FF',
    [THEME_TYPES.LIGHT]: '#0750A1'
  },
  info3ButtonHover: {
    [THEME_TYPES.DARK]: '#7CB5F2',
    [THEME_TYPES.LIGHT]: '#0F6DD2'
  },
  // Warning
  warningText: {
    [THEME_TYPES.DARK]: '#FFD970',
    [THEME_TYPES.LIGHT]: '#944901'
  },
  warningDecorative: {
    [THEME_TYPES.DARK]: '#FFD970',
    [THEME_TYPES.LIGHT]: '#CA7E04'
  },
  warningDecorative2: {
    [THEME_TYPES.DARK]: '#FBBA27',
    [THEME_TYPES.LIGHT]: '#FBBA27'
  },
  warningBackground: {
    [THEME_TYPES.DARK]: '#29251c',
    [THEME_TYPES.LIGHT]: '#FBF5EB'
  },
  // Error
  errorText: {
    [THEME_TYPES.DARK]: '#FF4245',
    [THEME_TYPES.LIGHT]: '#A10119'
  },
  errorDecorative: {
    [THEME_TYPES.DARK]: '#FF4245',
    [THEME_TYPES.LIGHT]: '#EA0129'
  },
  errorBackground: {
    [THEME_TYPES.DARK]: '#281a1e',
    [THEME_TYPES.LIGHT]: '#FEEBEE'
  },
  featureDecorative: {
    [THEME_TYPES.DARK]: '#70B4FF',
    [THEME_TYPES.LIGHT]: '#3851FF'
  },
  featureBackground: {
    [THEME_TYPES.DARK]: '#70B4FF1F',
    [THEME_TYPES.LIGHT]: '#ECF4FD'
  },
  iconPrimary: {
    [THEME_TYPES.DARK]: '#9E9E9F',
    [THEME_TYPES.LIGHT]: '#54597A'
  },
  iconSecondary: {
    [THEME_TYPES.DARK]: '#9E9E9F',
    [THEME_TYPES.LIGHT]: '#2D314D'
  },
  iconPrimary2: {
    [THEME_TYPES.DARK]: '#027BFF',
    [THEME_TYPES.LIGHT]: '#027BFF'
  },
  // Private mode accent (RAILGUN). The public mode accent is `primary`.
  // Shared by the Home v2 balance card, mode toggle, privacy donut and
  // selected-asset highlight when the wallet is in private mode.
  privateAccent: {
    [THEME_TYPES.DARK]: '#FF9230',
    [THEME_TYPES.LIGHT]: '#E07300'
  },
  privateAccent20: {
    [THEME_TYPES.DARK]: '#FF923033',
    [THEME_TYPES.LIGHT]: '#FF923014'
  },
  projectedRewards: {
    [THEME_TYPES.DARK]: '#D7FF00',
    [THEME_TYPES.LIGHT]: '#8B3DFF'
  },
  // Home v2 (mobile-web dashboard) surfaces. The page↔card↔border relationship
  // flips between modes — in dark the page is darkest and cards lift above it,
  // in light the page is a soft grey and cards are pure white — so these can't
  // be expressed by the shared primary/secondary pair. Light = the Figma design
  // values; dark = the previously shipped home look (page=primaryBackground,
  // card=secondaryBackground, border=primaryBorder), preserved here.
  homePageBackground: {
    [THEME_TYPES.DARK]: '#0D0D0F',
    [THEME_TYPES.LIGHT]: '#F5F7F9'
  },
  homeSidebarBackground: {
    [THEME_TYPES.DARK]: '#0D0D0F',
    [THEME_TYPES.LIGHT]: '#FFFFFF'
  },
  homeCardBackground: {
    [THEME_TYPES.DARK]: '#111318',
    [THEME_TYPES.LIGHT]: '#FFFFFF'
  },
  homeCardBorder: {
    [THEME_TYPES.DARK]: '#FFFFFF1F',
    [THEME_TYPES.LIGHT]: '#DFE8F4'
  },
  homeNavActiveBackground: {
    [THEME_TYPES.DARK]: '#111318',
    [THEME_TYPES.LIGHT]: '#EAF5FF'
  },
  // Sidebar nav-row hover — a subtle neutral tint, lighter than the active fill
  // and far softer than the washed-out quaternaryBackground (#FFFFFF20).
  homeNavHoverBackground: {
    [THEME_TYPES.DARK]: '#FFFFFF0D',
    [THEME_TYPES.LIGHT]: '#767DAD14'
  },
  homeControlBackground: {
    [THEME_TYPES.DARK]: '#111318',
    [THEME_TYPES.LIGHT]: '#EDF2FC'
  },
  // Raised amount+token field inside the mobile-web Send card (sits on top of
  // homeCardBackground). Lighter than the card so the input reads as a control.
  homeAmountFieldBackground: {
    [THEME_TYPES.DARK]: '#22272F',
    [THEME_TYPES.LIGHT]: '#F2F3FA'
  },
  white: {
    [THEME_TYPES.DARK]: '#FFFFFF',
    [THEME_TYPES.LIGHT]: '#FFFFFF'
  },
  // Onboarding / auth surfaces (redesigned dark-first; light values approximate
  // and can be tuned). Shared by OnboardingCard, ImportMethodRow and the
  // Welcome action buttons so light/dark stays a single-file change.
  onboardingCardBackground: {
    [THEME_TYPES.DARK]: '#111318',
    [THEME_TYPES.LIGHT]: '#FFFFFF'
  },
  onboardingRowBackground: {
    [THEME_TYPES.DARK]: '#191D24',
    [THEME_TYPES.LIGHT]: '#F2F3FA'
  },
  onboardingRowBackgroundHover: {
    [THEME_TYPES.DARK]: '#23272F',
    [THEME_TYPES.LIGHT]: '#E8EAF2'
  },
  onboardingIconBackground: {
    [THEME_TYPES.DARK]: '#22272F',
    [THEME_TYPES.LIGHT]: '#E8EAF2'
  },
  onboardingImportButtonBackground: {
    [THEME_TYPES.DARK]: '#001D4D4D',
    [THEME_TYPES.LIGHT]: '#EAF3FF'
  },
  // Page (behind the centered card) for the onboarding/auth/unlock flow. Light
  // is white (the full-screen onboarding layout reads as a flat white page);
  // dark keeps the existing near-black page.
  onboardingPageBackground: {
    [THEME_TYPES.DARK]: '#0D0D0F',
    [THEME_TYPES.LIGHT]: '#FFFFFF'
  },
  // Drop shadow under the onboarding/unlock card. Light = soft blue-grey; dark =
  // transparent (no shadow needed against the dark page).
  onboardingCardShadow: {
    [THEME_TYPES.DARK]: 'rgba(0, 0, 0, 0)',
    [THEME_TYPES.LIGHT]: 'rgba(99, 115, 148, 0.18)'
  },
  // Neutral chip behind a settings-row icon when rows are grouped into a card
  // (General settings, mobile-web). Sits one step lighter than the card surface.
  iconChipBackground: {
    [THEME_TYPES.DARK]: 'rgba(255, 255, 255, 0.05)',
    [THEME_TYPES.LIGHT]: 'rgba(118, 125, 173, 0.10)'
  },
  // Hairline divider between rows inside a grouped settings card. Lighter than
  // primaryBorder (the card outline) so the inner rules read as subordinate.
  settingsDivider: {
    [THEME_TYPES.DARK]: 'rgba(255, 255, 255, 0.08)',
    [THEME_TYPES.LIGHT]: 'rgba(118, 125, 173, 0.14)'
  },
  // "One last check" checklist panel (Shield / Unshield right column). Dark values
  // are the Figma design tokens (Border/Primary, Border/Tertiary, Content/Disable
  // and the orange-tinted warning surface); light mirrors the home-card palette.
  checklistCardBorder: {
    [THEME_TYPES.DARK]: '#15181E',
    [THEME_TYPES.LIGHT]: '#DFE8F4'
  },
  checklistDivider: {
    [THEME_TYPES.DARK]: '#21262D',
    [THEME_TYPES.LIGHT]: '#E8EAF2'
  },
  checklistWarningBackground: {
    [THEME_TYPES.DARK]: '#2B2621',
    [THEME_TYPES.LIGHT]: '#FDF1E6'
  },
  // Neutral "+N" pill capping an overlapping network-icon stack.
  chainStackOverflowBackground: {
    [THEME_TYPES.DARK]: '#2E3138',
    [THEME_TYPES.LIGHT]: '#E2E5F0'
  }
}

export default ThemeColors
